import {
  Box,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import GenericPageTitle from "modus/components/generic-page-title/GenericPageTitle";
import { ErrorMessage } from "modus/components/error-message/ErrorMessage";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { DeploymentDetails } from "./DeploymentDetails";
import { ThrottlingDetails } from "./ThrottlingDetails";
import { DeploymentCreator } from "../pages/application-summary-page/DeploymentCreator";
import ApiService, {
  IVersionDeployment,
  IVersionDeploymentResponse,
} from "../services/ApiService";
import { getResolvedEnv } from "./EnvBadge";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import Skeleton from "@material-ui/lab/Skeleton/Skeleton";
import NoAuthConfig from "./NoAuthConfiguration";
import { SpecUploader } from "./SpecUploader";
import { SpecDetails } from "./SpecDetails";
import { DeploymentRemover } from "./DeploymentRemover";
import {ResourceAuthorizer} from "../../authorizer/components/Authorizer";
import {useParams} from "react-router-dom";
import {useAuthorization} from "../../authorizer/components/AuthorizationProvider";

interface DeploymentListProps {
  version: string;
  consumerKey: string;
  applicationName: string;
  basePath: string;
  serviceId: string;
}

const useVersionDeployment = ()=>{

  const [consumerKey, setConsumerKey] = useState<string>();
  const [version, setVersion] = useState<string>();

   const [state, { subscribe }] = useAsyncAction(() =>
    ApiService.getVersionDeployment(consumerKey as string, version as string)
  );

  const getVersionDeployment = useCallback((consumerKey, version) => {
      setConsumerKey(consumerKey);
      setVersion(version);
  },[setConsumerKey, setVersion]);

  useEffect(()=>{
    if(!consumerKey || !version) {
      return;
    }
    subscribe();
  }, [consumerKey, version]);

  return {state, getVersionDeployment, refresh: subscribe};
}

export const DeploymentList: React.FC<DeploymentListProps> = React.memo(({
  version,
  consumerKey,
  applicationName,
  basePath,
  serviceId,
}) => {
  const [selectedDeployment, setSelectedDeployment] =
    useState<IVersionDeploymentResponse>();


  const [deployments, setDeployments] = useState<IVersionDeploymentResponse[]>(
    []
  );

  const [deployedRegions, setDeployedRegions] = useState<string[]>();

  const [specFile, setSpecFile] = useState<string>();

  const [specId, setSpecId] = useState<string>("");

  const changeDeployment = (deployment: IVersionDeploymentResponse) => {
    setSelectedDeployment(deployment);
  };

  const {state, getVersionDeployment, refresh } = useVersionDeployment();

  useEffect(() => {
    if (!version || !consumerKey) {
      return;
    }

    
    if(state?.value && version === Object.keys(state?.value)[0]){
      return; 
    }
    getVersionDeployment(consumerKey, version);
  }, [version, consumerKey, getVersionDeployment]);

  useEffect(() => {
    if (!state) {
      return;
    }
    if (state?.loaded) {
      const responseData = state?.value as unknown as {
        [key: string]: IVersionDeployment;
      };
      const deploymentsArray = responseData?.[version]?.deployments || [];
      setDeployments(deploymentsArray || []);
      setSpecFile(responseData?.[version]?.specFile);
      setSpecId(responseData?.[version]?.specId);
    }
  }, [state]);

  useEffect(() => {
    if (!deployments) {
      return;
    }
    if (deployments && deployments.length > 0) {
      setSelectedDeployment(deployments[0]);

      setDeployedRegions(
        deployments
          .map((deployment) => deployment?.location)
          .filter((location): location is string => location !== undefined)
      );
    } else {
      setSelectedDeployment(undefined);
      setDeployedRegions([]);
    }
  }, [deployments]);

  const { isActionAllowed } = useAuthorization("SERVICE" , serviceId );
  const canUpdate = isActionAllowed("update");

  return (
    <Box>
      <Paper style={{ width: "100%" }}>
        <Box padding={"20px"}>
          {deployments?.length >= 1 && (
            <Box style={{display: "flex", flexDirection: "row", justifyContent: "space-between", marginBottom: "15px"}}>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  Deployment
                </Typography>
            
                {selectedDeployment?.location ? (
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent={"space-between"}
                  >
                    <Select
                      id="region"
                      value={selectedDeployment?.location}
                      IconComponent={ExpandMoreIcon}
                      variant="outlined"
                      className="header-selector resource-version-select"
                    >
                      {deployments &&
                        deployments.length > 0 &&
                        deployments.map((deployment, index: number) => (
                          <MenuItem
                            value={deployment.location}
                            key={index}
                            onClick={() => changeDeployment(deployment)}
                          >
                            {getResolvedEnv(deployment.location as any)}
                          </MenuItem>
                        ))}
                    </Select>
                    <Box ml="15px" />
                    {!state?.loading && (
                        <ResourceAuthorizer action={"update"} resourceType={"SERVICE"} resourceId={serviceId} hideErrorMessage>
                        <DeploymentCreator
                        resourceAppName={applicationName}
                        version={version}
                        resourceAppId={consumerKey}
                        refreshDeploymentList={refresh}
                        deployedRegions={deployedRegions}
                        basePath={basePath}
                      />
                        </ResourceAuthorizer>
                    )}
                  </Box>
                ) : undefined}

              </Box>
          )}

          {state?.loading && (
            <Skeleton
              animation="wave"
              height="200px"
              width="100%"
              style={{ marginBottom: 6 }}
            />
          )}

          {!state?.loading &&
            deployments &&
            deployments.length > 0 &&
            selectedDeployment && (
              <DeploymentDetails
                  serviceId={serviceId}
                deploymentId={selectedDeployment?.deploymentId}
                timeout={String(selectedDeployment?.timeout || "")}
                deployedEndpoint={selectedDeployment?.deployedUrl || ""}
                deploymentStatus={selectedDeployment?.deployStatus}
                region={selectedDeployment?.location || ""}
                targetEndpoint={selectedDeployment?.backendUrl || ""}
                refreshDeployments={refresh}
              />
            )}
          {!state?.loading &&
            (version?.length === 0 ||  deployments===undefined || deployments?.length === 0) && (
              <ErrorMessage
                message={ canUpdate ?

                  <Box display="flex" flexDirection="column">
                    Add a deployment
                    <Box mt={1} />
                    {/** Create New deployment - component need to be added here */}
                    <DeploymentCreator
                      resourceAppName={applicationName}
                      version={version}
                      resourceAppId={consumerKey}
                      refreshDeploymentList={refresh}
                      deployedRegions={deployedRegions}
                      basePath={basePath}
                    />
                  </Box>
                    :  <Box display="flex" flexDirection="column">
                      User not authorized to deploy API version.
                    </Box>
                }
              />
            )}
        </Box>
        {
          <Paper style={{ width: "100%" }}>
            {deployments?.length >= 1 &&
              selectedDeployment?.deploymentId &&
              !state?.loading && (
                <ThrottlingDetails
                    serviceId={serviceId}
                  deployStatus={selectedDeployment?.deployStatus}
                  deploymentId={selectedDeployment?.deploymentId}
                  refreshDeploymentList={refresh}
                  apiThrottling={selectedDeployment?.apiThrottling as any}
                ></ThrottlingDetails>
              )}
          </Paper>
        }

        <Paper style={{ width: "100%" }}>
          {deployments?.length >= 1 &&
            selectedDeployment?.deploymentId &&
            !state?.loading && (
              <NoAuthConfig
                deployedUrl={selectedDeployment?.deployedUrl}
                deploymentId={selectedDeployment.deploymentId}
                refreshDeployments={refresh}
                noAuthResponse={selectedDeployment?.noAuth || undefined}
                deploymentStatus={selectedDeployment?.deployStatus}
                serviceId={serviceId}
              />
            )}
        </Paper>
        {deployments?.length >= 1 && !state?.loading && (
          <Box
            width={"100%"}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "20px",
            }}
          >
            <ResourceAuthorizer action={"delete"} resourceType={"SERVICE"} resourceId={serviceId} hideErrorMessage>

            <DeploymentRemover
              deploymentId={selectedDeployment?.deploymentId || ""}
              refreshDeployments={refresh}
              isDisabled={selectedDeployment?.deployStatus === "DEPLOY_INITIATED" || selectedDeployment?.deployStatus === "FAILED"}
            />
            </ResourceAuthorizer>
          </Box>
        )}
      </Paper>
    </Box>
  );
});
